import { AfterViewInit, Component, computed, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { catchError, Observable, of, ReplaySubject, finalize, takeUntil, map } from 'rxjs';
import { SharingConfirmComponent } from '../sharing-confirm/sharing-confirm.component';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { User } from '../../../../shared/types/user.model';
import { KorviuMultiSelectComponent } from '../../../../shared/components/korviu-multi-select/korviu-multi-select.component';
import { ReferralModalComponent } from '../../referral-modal/referral-modal.component';
import { KorviuInputComponent } from '../../../../shared/components/korviu-input/korviu-input.component';
import { KorviuLoaderComponent } from '../../../../shared/components/korviu-loader/korviu-loader.component';
import { KorviuNoDataComponent } from '../../../../shared/components/korviu-no-data/korviu-no-data.component';
import { KorviuButtonComponent } from '../../../../shared/components/korviu-button/korviu-button.component';
import { KorviuListComponent } from '../../../../shared/components/korviu-list/korviu-list.component';
import { ReferralModalFacade } from '../../services/referral-modal.facade';
import { UsersService } from 'core';
import { BalanceService } from 'core/services/balance.service';
import { ThousandsSeparatorPipe } from 'shared/pipes/thousands-separator.pipe';

@Component({
  selector: 'app-share-ganas-modal',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    NzModalModule,
    NzButtonModule,
    NzAvatarModule,
    KorviuInputComponent,
    KorviuMultiSelectComponent,
    KorviuLoaderComponent,
    KorviuNoDataComponent,
    KorviuButtonComponent,
    ThousandsSeparatorPipe
  ],
  templateUrl: './share-ganas-modal.component.html',
  styleUrl: './share-ganas-modal.component.scss'
})
export class ShareGanasModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('ganamigoSelect') public ganamigoSelect: KorviuMultiSelectComponent;

  readonly isInDeficit = computed(() => this._balanceService.isInDeficit());
  readonly deficitGanas = computed(() => this._balanceService.transactionSummary()?.deficit ?? 0);

  public selectedGanamigo: number;
  public recentGanamigos: User[];
  public recentGanamigosIsLoading: boolean;
  public balanceIsLoading: boolean;
  public amountTosendControl: FormControl;
  private _onDestroy$: ReplaySubject<void>;
  private readonly _deletedRecipientIds = new Set<number>();
  private _prefillTimer: ReturnType<typeof setTimeout>;

  public get shareButtonDisabled(): boolean {
    return (
      this.balanceIsLoading ||
      this.isInDeficit() ||
      !!this.amountTosendControl?.invalid ||
      !this.selectedGanamigo ||
      this._deletedRecipientIds.has(this.selectedGanamigo) ||
      (this.data?.recipient?.id === this.selectedGanamigo && this.data.recipient.isDeleted)
    );
  }

  public get availableGanas(): number {
    return this._balanceService.spendableGanas();
  }

  constructor(
    @Inject(NZ_MODAL_DATA) public data: { recipient?: User; transactionList?: KorviuListComponent },
    public _modal: NzModalRef,
    private _modalService: NzModalService,
    private _translateService: TranslateService,
    private fb: FormBuilder,
    private _referralModalFacade: ReferralModalFacade,
    private _usersService: UsersService,
    private _balanceService: BalanceService
  ) {
    this.recentGanamigos = [];
    this.recentGanamigosIsLoading = false;
    this.balanceIsLoading = true;
    this.amountTosendControl = this.fb.control('', [Validators.required, this._valueLessThan()]);
    this._onDestroy$ = new ReplaySubject<void>(1);
  }

  public ngOnInit(): void {
    this._loadTransactionSummary();
    this._loadRecentGanamigos();
  }

  public ngAfterViewInit(): void {
    this._prefillTimer = setTimeout(() => {
      if (this.data?.recipient && !this.data.recipient.isDeleted) {
        this.ganamigoSelect.prefillField(this.data?.recipient);
        this.selectedGanamigo = this?.data?.recipient?.id;
      }
    });
  }

  public ngOnDestroy(): void {
    clearTimeout(this._prefillTimer);
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public dropdownLabelFormatFn(ganamigo: User): string {
    const status = ganamigo?.userObj?.status || ganamigo?.typeName;

    if (status !== 'Ganamigo') {
      return `${ganamigo?.userObj?.full_name} - ${this._translateService.instant('myGanarbols.levelId')}${ganamigo.levelNumber} ${status}`;
    }

    return `${ganamigo?.userObj?.full_name} - ${status}`;
  }

  public onGanamigoClick(user: User): void {
    if (user?.isDeleted) {
      this._deletedRecipientIds.add(user.id);
      return;
    }
    this._scrollToAmountInput();
    this.ganamigoSelect.prefillField(user);
    this.selectedGanamigo = user?.id;
  }

  public getUserNameForAvatar(name: string): string {
    const initials: string[] = name.split(' ').filter(Boolean);
    return (initials?.[0]?.[0] || '') + (initials?.[1]?.[0] || '');
  }

  public onCancel(): void {
    this._modal.destroy();
  }

  public onShareClick(): void {
    if (!this.shareButtonDisabled) {
      this._modal.close();
      this._modalService
        .create({
          nzContent: SharingConfirmComponent,
          nzWidth: 554,
          nzWrapClassName: 'modal-wrap',
          nzCentered: true,
          nzClosable: false,
          nzData: {
            recipientId: this.selectedGanamigo,
            amountToSend: this.amountTosendControl.value,
            transactionList: this.data?.transactionList
          }
        })
        .afterClose.pipe(takeUntil(this._onDestroy$))
        .subscribe((result: boolean) => {
          if (result) {
            this._modal.close(result);
          }
        });
    }
  }

  public openSendInviteModal(): void {
    this._referralModalFacade.openAddReferralModal();
    this._modal.close();
  }

  public onSharePeopleSearch(search: string): Observable<User[]> {
    return this._usersService
      .getNetworkUsers(search.trim(), { pageSize: 10, page: 1 })
      .pipe(map(users => this._eligibleRecipients(users)));
  }

  private _loadRecentGanamigos(): void {
    this.recentGanamigosIsLoading = true;
    this._usersService
      .getNetworkUsers(undefined, { pageSize: 10, page: 1 })
      .pipe(
        finalize(() => {
          this.recentGanamigosIsLoading = false;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((users: User[]) => {
        this.recentGanamigos = this._eligibleRecipients(users).slice(0, 10);
      });
  }

  private _eligibleRecipients(users: User[]): User[] {
    for (const user of users) {
      if (user.isDeleted) {
        this._deletedRecipientIds.add(user.id);
      }
    }
    return users.filter(user => !user.isDeleted);
  }

  private _loadTransactionSummary(): void {
    this.balanceIsLoading = true;
    this._balanceService
      .loadTransactionSummary(true)
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.balanceIsLoading = false;
          this.amountTosendControl.updateValueAndValidity();
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }

  private _valueLessThan(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isInDeficit()) {
        return { balanceInDeficit: true };
      }

      const currentValue: number = control.value;
      if (currentValue === null || currentValue <= 0) {
        return { valueLessThanOrEqualZero: true };
      }
      if (currentValue !== null && this.availableGanas !== null && currentValue > this.availableGanas) {
        return { valueGreaterThanMax: true };
      }
      return null;
    };
  }

  private _scrollToAmountInput(): void {
    const amontInput = this._modal.getElement().querySelector('#share-ganas-amount-input');
    if (amontInput) {
      amontInput.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }
}
